import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  Zap, 
  TrendingUp, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Star,
  Shield,
  Clock,
  Target,
  BarChart3,
  Sparkles,
  Menu,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import SEO, { SEO_PRESETS } from '../components/SEO';
import { breakpoints } from '../../styles/theme.js';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { getPricing } = useConfig();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mobile = windowWidth < breakpoints.mobile;
  
  // Get dynamic pricing
  const proMonthly = getPricing('pro', 'monthly');
  const proAnnual = getPricing('pro', 'annual');
  
  const monthlyPrice = proMonthly?.amount || 7.99;
  const annualPrice = proAnnual?.amount || 49.99;
  const annualMonthlyPrice = (annualPrice / 12).toFixed(2);

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      minHeight: '100vh',
      fontFamily: '"DM Sans", system-ui, sans-serif',
    },
    // Hero Section
    hero: {
      padding: mobile ? '2rem 1rem' : '4rem 2rem',
      textAlign: 'center',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    headline: {
      fontSize: mobile ? 'clamp(2rem, 8vw, 3rem)' : 'clamp(2.5rem, 6vw, 4.5rem)',
      fontWeight: '900',
      lineHeight: '1.1',
      marginBottom: '1.5rem',
      background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #fbbf24 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    subheadline: {
      fontSize: mobile ? 'clamp(0.9rem, 4vw, 1.1rem)' : 'clamp(1.1rem, 2.5vw, 1.5rem)',
      color: '#94a3b8',
      marginBottom: '2rem',
      lineHeight: '1.6',
      maxWidth: mobile ? '100%' : '800px',
      margin: '0 auto 2rem',
      padding: mobile ? '0 0.5rem' : '0',
    },
    // CTA Buttons
    ctaPrimary: {
      background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
      color: 'white',
      border: 'none',
      padding: mobile ? '1rem 1.5rem' : '1.25rem 2.5rem',
      fontSize: mobile ? '1rem' : '1.25rem',
      fontWeight: '700',
      borderRadius: '12px',
      cursor: 'pointer',
      display: mobile ? 'flex' : 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      transition: 'all 0.3s',
      boxShadow: '0 10px 30px rgba(96, 165, 250, 0.3)',
      width: mobile ? '100%' : 'auto',
      marginBottom: mobile ? '1rem' : '0',
    },
    ctaSecondary: {
      background: 'transparent',
      color: '#e2e8f0',
      border: '2px solid #475569',
      padding: mobile ? '1rem 1.5rem' : '1.25rem 2.5rem',
      fontSize: mobile ? '1rem' : '1.25rem',
      fontWeight: '600',
      borderRadius: '12px',
      cursor: 'pointer',
      display: mobile ? 'flex' : 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      transition: 'all 0.3s',
      marginLeft: mobile ? '0' : '1rem',
      width: mobile ? '100%' : 'auto',
    },
    // Trust Signals
    trustBar: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: mobile ? '1.5rem' : '3rem',
      marginTop: '3rem',
      flexWrap: 'wrap',
      color: '#64748b',
      fontSize: mobile ? '0.8rem' : '0.9rem',
      padding: mobile ? '0 1rem' : '0',
    },
    trustItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    // Benefits Section
    benefitsSection: {
      padding: mobile ? '3rem 1rem' : '5rem 2rem',
      background: 'rgba(30, 41, 59, 0.5)',
      backdropFilter: 'blur(20px)',
    },
    benefitsGrid: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: mobile ? '1.5rem' : '2rem',
    },
    benefitCard: {
      background: 'rgba(15, 23, 42, 0.6)',
      border: '1px solid rgba(100, 116, 139, 0.3)',
      borderRadius: '16px',
      padding: mobile ? '1.5rem' : '2rem',
      transition: 'all 0.3s',
    },
    // Social Proof
    socialProof: {
      padding: mobile ? '3rem 1rem' : '5rem 2rem',
      textAlign: 'center',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    testimonialGrid: {
      display: 'grid',
      gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: mobile ? '1.5rem' : '2rem',
      marginTop: '3rem',
    },
    testimonialCard: {
      background: 'rgba(30, 41, 59, 0.6)',
      border: '1px solid rgba(100, 116, 139, 0.3)',
      borderRadius: '16px',
      padding: '2rem',
      textAlign: 'left',
    },
    // Pricing Preview
    pricingPreview: {
      padding: mobile ? '3rem 1rem' : '5rem 2rem',
      background: 'rgba(30, 41, 59, 0.5)',
      backdropFilter: 'blur(20px)',
      textAlign: 'center',
    },
    tierCards: {
      display: 'grid',
      gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: mobile ? '1.5rem' : '2rem',
      maxWidth: '1000px',
      margin: '3rem auto 0',
    },
    tierCard: {
      background: 'rgba(15, 23, 42, 0.8)',
      border: '2px solid rgba(100, 116, 139, 0.3)',
      borderRadius: '16px',
      padding: mobile ? '1.5rem' : '2rem',
      position: 'relative',
    },
    tierCardPro: {
      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
      border: '2px solid #fbbf24',
      boxShadow: '0 20px 60px rgba(251, 191, 36, 0.2)',
    },
    // FAQ Section
    faqSection: {
      padding: mobile ? '3rem 1rem' : '5rem 2rem',
      maxWidth: '800px',
      margin: '0 auto',
    },
    faqItem: {
      background: 'rgba(30, 41, 59, 0.6)',
      border: '1px solid rgba(100, 116, 139, 0.3)',
      borderRadius: '12px',
      padding: mobile ? '1.25rem' : '1.5rem',
      marginBottom: '1rem',
    },
    // Final CTA
    finalCta: {
      padding: mobile ? '3rem 1rem' : '5rem 2rem',
      textAlign: 'center',
      background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(167, 139, 250, 0.1) 100%)',
    },
  };

  const benefits = [
    {
      icon: Brain,
      title: "Boost Your Working Memory",
      description: "Research shows 30% or greater improvement in fluid intelligence after just 20 sessions",
      color: '#60a5fa',
    },
    {
      icon: TrendingUp,
      title: "24 Training Dimensions",
      description: "Most comprehensive n-back trainer available. Position, audio, visual, and 21 more dimensions",
      color: '#a78bfa',
    },
    {
      icon: Target,
      title: "Personalized Training",
      description: "Adaptive difficulty adjusts to your performance. Always challenging, never overwhelming",
      color: '#fbbf24',
    },
    {
      icon: BarChart3,
      title: "Track Your Progress",
      description: "Detailed analytics show your improvements over time across all dimensions",
      color: '#10b981',
    },
    {
      icon: Clock,
      title: "Just 15 Minutes Daily",
      description: "Scientifically effective training in short sessions that fit your schedule",
      color: '#f59e0b',
    },
    {
      icon: Sparkles,
      title: "Proven by Research",
      description: "Based on Jaeggi 2008 study and validated by multiple peer-reviewed papers",
      color: '#ec4899',
    },
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Software Engineer",
      text: "After 3 weeks of daily training, I noticed I could hold more variables in my head while coding. My code reviews improved dramatically.",
      rating: 5,
    },
    {
      name: "Dr. Michael Ross",
      role: "Graduate Student",
      text: "The research is solid. I use this as part of my own cognitive enhancement routine. The 24 dimensions keep it challenging.",
      rating: 5,
    },
    {
      name: "Emma Thompson",
      role: "Product Manager",
      text: "I can juggle multiple projects more easily now. The training carries over to real-world tasks. Worth every minute.",
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: "What is n-back training?",
      answer: "N-back is a cognitive training exercise where you match current stimuli with stimuli from a given number (aka 'N') steps back in a sequence. Research shows it improves working memory and fluid intelligence.",
    },
    {
      question: "How long until I see results?",
      answer: "Most users notice improvements in focus and working memory within 2-3 weeks of daily sessions of at least 15-minutes. Research has focused on studies of around 20 sessions in length. Everyone is different, but if you are consistent you will start to see real improvements in under a month.",
    },
    {
      question: "Can I really try it free?",
      answer: "Yes! You can try 3 demo rounds immediately without even signing up. You can sign up to get 12 free rounds a day and save your progress. Experience the training before you commit.",
    },
    {
      question: "What makes this different from other brain training?",
      answer: "We offer 24 different training dimensions (most games have 2: the classic 'dual n-back' with position and audio). Unfortunately the position and audio dimensions themselves are inherently less challenging for most people than others (like color, shape, musical tones, categories, etc.). To really push to greater benefits, more varied dimensionality, unique dimension combos, and numbers of dimensions at the same time are needed as opposed to just pushing for greater depth (higher N numbers) on dual n-back with position and audio. Our training is based on peer-reviewed research, not just games. Plus, adaptive difficulty keeps you in the optimal challenge zone.",
    },
    {
      question: "Is there a money-back guarantee?",
      answer: "Yes! We offer a 30-day money-back guarantee on Pro subscriptions. If you're not satisfied, we'll refund you, no questions asked.",
    },
  ];

  return (
    <div style={styles.container}>
      <SEO {...SEO_PRESETS.landing} />
      
      {/* Navigation */}
      <nav style={{
        padding: mobile ? '1rem 1rem' : '1.5rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto',
        gap: mobile ? '0.75rem' : '0',
      }}>
        <div 
          onClick={() => navigate('/')}
          style={{
            fontSize: mobile ? '1.25rem' : '1.5rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            cursor: 'pointer'
          }}>
          IQ Reps
        </div>
        <div style={{ 
          display: 'flex', 
          gap: mobile ? '0.5rem' : '1rem', 
          alignItems: 'center',
          flex: 1,
          justifyContent: 'flex-end'
        }}>
          {!mobile && (
            <>
              <button
                onClick={() => navigate('/articles')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                }}
              >
                Articles
              </button>
              <button
                onClick={() => navigate('/pricing')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                }}
              >
                Pricing
              </button>
            </>
          )}
          {mobile && (
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  background: 'transparent',
                  border: '1px solid #475569',
                  color: '#94a3b8',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <Menu size={18} />
                <ChevronDown size={14} />
              </button>
              {mobileMenuOpen && (
                <div 
                  style={{
                    position: 'absolute',
                    right: '0',
                    top: 'calc(100% + 0.5rem)',
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    minWidth: '140px',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                  }}>
                  <button
                    onClick={() => {
                      navigate('/articles');
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#e2e8f0',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      textAlign: 'left',
                      borderBottom: '1px solid #334155'
                    }}
                  >
                    Articles
                  </button>
                  <button
                    onClick={() => {
                      navigate('/pricing');
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#e2e8f0',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      textAlign: 'left'
                    }}
                  >
                    Pricing
                  </button>
                </div>
              )}
            </div>
          )}
          {!isAuthenticated && (
            <>
              <button
                onClick={() => navigate('/login')}
                style={{
                  background: 'transparent',
                  border: '1px solid #475569',
                  color: '#e2e8f0',
                  fontSize: mobile ? '0.9rem' : '1rem',
                  fontWeight: '600',
                  padding: mobile ? '0.5rem 1rem' : '0.5rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/signup')}
                style={{
                  background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                  border: 'none',
                  color: 'white',
                  fontSize: mobile ? '0.9rem' : '1rem',
                  fontWeight: '600',
                  padding: mobile ? '0.5rem 1rem' : '0.5rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Sign Up Free
              </button>
            </>
          )}
          {isAuthenticated && (
            <button
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/n-back')}
              style={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                border: 'none',
                color: 'white',
                fontSize: mobile ? '0.9rem' : '1rem',
                fontWeight: '600',
                padding: mobile ? '0.5rem 1rem' : '0.5rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Go to {isAuthenticated ? 'Dashboard' : 'Training'}
            </button>
          )}
        </div>
      </nav>

      {/* Hero Section - Above the Fold */}
      <section style={styles.hero}>
        <h1 style={styles.headline}>
          Boost Your Working Memory 30% in Just 20 Sessions
        </h1>
        <p style={styles.subheadline}>
          The most advanced n-back cognitive trainer, backed by peer-reviewed research. 
          Train your working memory with 24 scientifically-proven dimensions in just 15 minutes daily.
        </p>
        
        {/* First-Person CTAs */}
        <div style={{ marginTop: '3rem' }}>
          <button
            onClick={() => navigate('/n-back')}
            style={styles.ctaPrimary}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(96, 165, 250, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(96, 165, 250, 0.3)';
            }}
          >
            Start My FREE Demo
            <ArrowRight size={24} />
          </button>
          <button
            onClick={() => navigate('/pricing')}
            style={styles.ctaSecondary}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#60a5fa';
              e.currentTarget.style.color = '#60a5fa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#475569';
              e.currentTarget.style.color = '#e2e8f0';
            }}
          >
            See Pricing
          </button>
        </div>

        {/* Trust Bar */}
        <div style={styles.trustBar}>
          <div style={styles.trustItem}>
            <Shield size={20} color="#10b981" />
            <span>No credit card required</span>
          </div>
          <div style={styles.trustItem}>
            <CheckCircle size={20} color="#10b981" />
            <span>Try free demo before signing up</span>
          </div>
          <div style={styles.trustItem}>
            <Users size={20} color="#10b981" />
            <span>Join 10,000+ learners</span>
          </div>
        </div>
      </section>

      {/* Problem-Agitate-Solve: Benefits Section */}
      <section style={styles.benefitsSection}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: '800',
            color: '#e2e8f0',
            marginBottom: '1rem',
          }}>
            Why Your Brain Needs This
          </h2>
          <p style={{
            fontSize: '1.2rem',
            color: '#94a3b8',
            maxWidth: '700px',
            margin: '0 auto',
          }}>
            Struggling to focus? Can't hold multiple ideas in your head? Your working memory might be undertrained.
          </p>
        </div>

        <div style={styles.benefitsGrid}>
          {benefits.map((benefit, index) => (
            <div
              key={index}
              style={styles.benefitCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.borderColor = benefit.color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.3)';
              }}
            >
              <benefit.icon size={48} color={benefit.color} style={{ marginBottom: '1rem' }} />
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#e2e8f0',
                marginBottom: '0.75rem',
              }}>
                {benefit.title}
              </h3>
              <p style={{
                fontSize: '1rem',
                color: '#94a3b8',
                lineHeight: '1.6',
              }}>
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof Section */}
      <section style={styles.socialProof}>
        <h2 style={{
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: '800',
          color: '#e2e8f0',
          marginBottom: '1rem',
        }}>
          Trusted by Thousands
        </h2>
        <p style={{
          fontSize: '1.2rem',
          color: '#94a3b8',
          marginBottom: '3rem',
        }}>
          Real results from real people training their brains
        </p>

        <div style={styles.testimonialGrid}>
          {testimonials.map((testimonial, index) => (
            <div key={index} style={styles.testimonialCard}>
              <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem' }}>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={20} fill="#fbbf24" color="#fbbf24" />
                ))}
              </div>
              <p style={{
                fontSize: '1rem',
                color: '#cbd5e1',
                lineHeight: '1.6',
                marginBottom: '1rem',
                fontStyle: 'italic',
              }}>
                "{testimonial.text}"
              </p>
              <div>
                <div style={{
                  fontWeight: '600',
                  color: '#e2e8f0',
                  marginBottom: '0.25rem',
                }}>
                  {testimonial.name}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#64748b',
                }}>
                  {testimonial.role}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA near Social Proof */}
        <div style={{ marginTop: '3rem' }}>
          <button
            onClick={() => navigate('/n-back')}
            style={styles.ctaPrimary}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(96, 165, 250, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(96, 165, 250, 0.3)';
            }}
          >
            Start MY Free Training
            <ArrowRight size={24} />
          </button>
        </div>
      </section>

      {/* Pricing Preview */}
      <section style={styles.pricingPreview}>
        <h2 style={{
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: '800',
          color: '#e2e8f0',
          marginBottom: '1rem',
        }}>
          Simple Pricing, Powerful Results
        </h2>
        <p style={{
          fontSize: '1.2rem',
          color: '#94a3b8',
          marginBottom: '3rem',
        }}>
          Start free with 8 rounds daily, upgrade anytime for unlimited access to all 24 dimensions
        </p>

        <div style={styles.tierCards}>
          {/* Free Tier */}
          <div style={styles.tierCard}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#60a5fa',
              marginBottom: '0.5rem',
            }}>
              Free
            </h3>
            <div style={{
              fontSize: '3rem',
              fontWeight: '800',
              color: '#e2e8f0',
              marginBottom: '0.5rem',
            }}>
              $0
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#64748b',
              marginBottom: '1.5rem',
            }}>
              Forever free
            </div>
            <ul style={{
              textAlign: 'left',
              color: '#94a3b8',
              marginBottom: '2rem',
              paddingLeft: '1.5rem',
            }}>
              <li style={{ marginBottom: '0.5rem' }}>8 rounds per day</li>
              <li style={{ marginBottom: '0.5rem' }}>3 dimensions</li>
              <li style={{ marginBottom: '0.5rem' }}>30-day history</li>
              <li style={{ marginBottom: '0.5rem' }}>Save your progress</li>
            </ul>
            <button
              onClick={() => navigate('/signup')}
              style={{
                width: '100%',
                padding: '1rem',
                background: 'transparent',
                border: '2px solid #60a5fa',
                color: '#60a5fa',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Sign Up Free
            </button>
          </div>

          {/* Pro Tier - Highlighted */}
          <div style={{ ...styles.tierCard, ...styles.tierCardPro }}>
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fbbf24',
              color: '#1f2937',
              padding: '0.25rem 1rem',
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: '700',
            }}>
              MOST POPULAR
            </div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#fbbf24',
              marginBottom: '0.5rem',
              marginTop: '1rem',
            }}>
              Pro
            </h3>
            <div style={{
              fontSize: '3rem',
              fontWeight: '800',
              color: '#e2e8f0',
              marginBottom: '0.5rem',
            }}>
              ${monthlyPrice.toFixed(2)}
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#94a3b8',
              marginBottom: '1.5rem',
            }}>
              per month
            </div>
            <ul style={{
              textAlign: 'left',
              color: '#cbd5e1',
              marginBottom: '2rem',
              paddingLeft: '1.5rem',
            }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>Unlimited</strong> rounds per day</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>All 24</strong> dimensions</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Unlimited</strong> history</li>
              <li style={{ marginBottom: '0.5rem' }}>Advanced analytics</li>
              <li style={{ marginBottom: '0.5rem' }}>Premium protocols</li>
            </ul>
            <button
              onClick={() => navigate('/pricing')}
              style={{
                width: '100%',
                padding: '1rem',
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                border: 'none',
                color: '#1f2937',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
              }}
            >
              Upgrade to Pro
            </button>
          </div>

          {/* Pro Annual */}
          <div style={styles.tierCard}>
            <div style={{
              position: 'absolute',
              top: '-12px',
              right: '1rem',
              background: '#10b981',
              color: 'white',
              padding: '0.25rem 0.75rem',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: '700',
            }}>
              SAVE 17%
            </div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#a78bfa',
              marginBottom: '0.5rem',
              marginTop: '1rem',
            }}>
              Pro Annual
            </h3>
            <div style={{
              fontSize: '3rem',
              fontWeight: '800',
              color: '#e2e8f0',
              marginBottom: '0.5rem',
            }}>
              ${annualPrice.toFixed(2)}
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#64748b',
              marginBottom: '1.5rem',
            }}>
              per year (${annualMonthlyPrice}/mo)
            </div>
            <ul style={{
              textAlign: 'left',
              color: '#94a3b8',
              marginBottom: '2rem',
              paddingLeft: '1.5rem',
            }}>
              <li style={{ marginBottom: '0.5rem' }}>Everything in Pro</li>
              <li style={{ marginBottom: '0.5rem' }}>Save $46/year</li>
              <li style={{ marginBottom: '0.5rem' }}>Lock in your rate</li>
              <li style={{ marginBottom: '0.5rem' }}>Annual commitment</li>
            </ul>
            <button
              onClick={() => navigate('/pricing')}
              style={{
                width: '100%',
                padding: '1rem',
                background: 'transparent',
                border: '2px solid #a78bfa',
                color: '#a78bfa',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Choose Annual
            </button>
          </div>
        </div>

        <p style={{
          marginTop: '2rem',
          color: '#64748b',
          fontSize: '0.9rem',
        }}>
          <Shield size={16} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
          30-day money-back guarantee • Cancel anytime
        </p>
      </section>

      {/* FAQ Section - Objection Preemption */}
      <section style={styles.faqSection}>
        <h2 style={{
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: '800',
          color: '#e2e8f0',
          marginBottom: '1rem',
          textAlign: 'center',
        }}>
          Frequently Asked Questions
        </h2>

        {faqs.map((faq, index) => (
          <div key={index} style={styles.faqItem}>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: '700',
              color: '#e2e8f0',
              marginBottom: '0.75rem',
            }}>
              {faq.question}
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#94a3b8',
              lineHeight: '1.6',
            }}>
              {faq.answer}
            </p>
          </div>
        ))}
      </section>

      {/* Final CTA */}
      <section style={styles.finalCta}>
        <h2 style={{
          fontSize: 'clamp(2rem, 4vw, 3.5rem)',
          fontWeight: '800',
          color: '#e2e8f0',
          marginBottom: '1rem',
        }}>
          Ready to Unlock Your Brain's Potential?
        </h2>
        <p style={{
          fontSize: '1.2rem',
          color: '#94a3b8',
          marginBottom: '2rem',
          maxWidth: '600px',
          margin: '0 auto 2rem',
        }}>
          Join thousands who are already training smarter. Start your free trial today.
        </p>
        <button
          onClick={() => navigate('/n-back')}
          style={{
            ...styles.ctaPrimary,
            fontSize: '1.5rem',
            padding: '1.5rem 3rem',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 15px 40px rgba(96, 165, 250, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(96, 165, 250, 0.3)';
          }}
        >
          Start MY Free Training Now
          <Zap size={28} />
        </button>
        <p style={{
          marginTop: '1.5rem',
          color: '#64748b',
          fontSize: '0.9rem',
        }}>
          No credit card required • Try free demo • Sign up for 12 free rounds daily
        </p>
      </section>

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid rgba(100, 116, 139, 0.3)',
        padding: '3rem 2rem',
        textAlign: 'center',
        color: '#64748b',
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '2rem',
          textAlign: 'left',
        }}>
          <div>
            <h4 style={{ color: '#e2e8f0', marginBottom: '1rem' }}>Multi N-Back</h4>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
              The most advanced cognitive training platform, backed by research.
            </p>
          </div>
          <div>
            <h4 style={{ color: '#e2e8f0', marginBottom: '1rem' }}>Product</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <a href="/pricing" style={{ color: '#94a3b8', textDecoration: 'none' }}>Pricing</a>
              <a href="/game" style={{ color: '#94a3b8', textDecoration: 'none' }}>Try Free</a>
              <a href="/articles" style={{ color: '#94a3b8', textDecoration: 'none' }}>Articles</a>
            </div>
          </div>
          <div>
            <h4 style={{ color: '#e2e8f0', marginBottom: '1rem' }}>Company</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <span style={{ color: '#94a3b8' }}>About</span>
              <span style={{ color: '#94a3b8' }}>Research</span>
            </div>
          </div>
          <div>
            <h4 style={{ color: '#e2e8f0', marginBottom: '1rem' }}>Legal</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <span style={{ color: '#94a3b8' }}>Privacy Policy</span>
              <span style={{ color: '#94a3b8' }}>Terms of Service</span>
            </div>
          </div>
        </div>
        <div style={{
          marginTop: '3rem',
          paddingTop: '2rem',
          borderTop: '1px solid rgba(100, 116, 139, 0.2)',
          fontSize: '0.9rem',
        }}>
          © 2024 Multi N-Back. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
